<template>
  <!-- Table Container Card -->
  <b-card no-body>

    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <Button
            v-if="getUserPermission('ADD_WHITELIST')"
            :label="$t('Add')"
            size="md"
            @click="showModal('modal-lg')"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      ref="blackListRef"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      :items="whiteListData"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">
        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="getUserPermission('REMOVE_WHITELIST')"
              @click="onRemoveItem(data.item, $event.target)"
            >
              <feather-icon icon="DeleteIcon" />
              <span class="align-middle ml-50"> {{ $t('Delete') }} </span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-lg"
      ref="my-modal"
      :ok-title="buttonLabel"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      no-close-on-backdrop
      size="lg"
      :title="modalTitle"
      :ok-disabled="isLoading"
      @ok="handleSubmit"
      @hidden="resetModal"
    >

      <validation-observer ref="whiteListRules">
        <b-form>
          <b-row>
            <b-col md="5">
              <InputField
                id="debit-field"
                v-model="accountSearchParams.name"
                :label="$t('Account holder')"
                :placeholder="$t('Enter account holder')"
                rules="required"
                type="text"
              />
            </b-col>
            <b-col md="5">
              <SelectField
                id="debit-search-field"
                v-model="accountSearchParams.field"
                :label="$t('Search')"
                :options="translatedSearchFieldOptions"
                :reduce="option => option.value"
                :error-name="$t('Search')"
                rules="required"
                item="label"
                :placeholder="$t('Select')"
              />
            </b-col>
            <b-col md="2">
              <Button
                :label="$t('Search')"
                size="md"
                class="mt-2"
                block
                @click="handleCustomerSearch"
              />
            </b-col>
          </b-row>
          <b-row>
            <b-col
              md="10"
              class="mt-2"
            >
              <InputField
                id="account-name"
                v-model="whiteListFormData.accountName"
                :label="$t('Account Name')"
                :placeholder="$t('Enter account name')"
                type="text"
                for="name"
                readonly
              />
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
      <FullScreenSpinner :show="showSpinner" />
    </b-modal>
    <!-- modal -->

    <b-modal
      id="search-modal"
      ref="search-modal-lg"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="$t('Customer Details')"
      cancel-title-only
      @cancel="resetModal"
    >

      <b-table
        ref="customerListTable"
        select-mode="single"
        :items="customerList"
        responsive
        :fields="translatedSearchTableHeaders"
        primary-key="id"
        selectable
        show-empty
        :empty-text="$t('No result found')"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        class="position-relative"
        @row-selected="onSelectCustomer"
      >

        <template #cell(SN)="data">
          {{ getOverallIndex(data.index) }}
        </template>

      </b-table>

      <TablePagination
        v-model="currentPage"
        :from="from"
        :to="to"
        :total-rows="totalEntries"
        :per-page="perPage"
      />

      <template
        #modal-footer="{ }"
      >
        <div />
      </template>

    </b-modal>

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      no-close-on-backdrop
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchWhiteList"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!-- Account Number -->
        <b-col md="6">
          <InputField
            id="account-number"
            v-model="filter.accountNumber"
            :label="$t('Account Number')"
            :placeholder="$t('Enter account number')"
            type="text"
          />
        </b-col>
        <!-- Account Number -->

        <!-- Bank Code -->
        <b-col md="6">
          <InputField
            id="bank-code"
            v-model="filter.bankCode"
            :label="$t('Bank Code')"
            :placeholder="$t('Enter bank code')"
            type="text"
          />
        </b-col>
        <!-- Bank Code -->

      </b-row>
    </b-modal>
    <!-- Filter modal -->
    <FullScreenSpinner :show="showSpinner" />
  </b-card>

</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BTable,
  BSpinner,
  BModal,
  BForm,
  BDropdownItem,
  BDropdown,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapActions, mapGetters } from 'vuex'
import { permissionMixin } from '@core/mixins/permission'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import { fraudMonitorPermissions } from '@/helpers/permissions'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import InputField from '../../forms/InputField.vue'
import SelectField from '../../forms/SelectField.vue'
import Button from '../../components/button/Button.vue'

const searchFieldOptions = [
  { label: 'Account Name', value: 'accountName' },
  { label: 'Account Number', value: 'accountNumber' },
  { label: 'Customer ID', value: 'customerID' },
  { label: 'Account Type', value: 'accountType' },
  { label: 'Reference Number', value: 'referenceNumber' },
]

export default {
  components: {
    BCard,
    BRow,
    BCol,
    SelectField,
    BTable,
    BModal,
    BForm,
    BSpinner,
    ValidationObserver,
    InputField,
    BDropdownItem,
    BDropdown,
    TablePagination,
    Button,
    TableEntries,
    FullScreenSpinner,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      query: '',
      isReadonly: false,
      isEditable: false,
      showSpinner: false,
      sortBy: 'name',
      buttonLabel: this.$t('Save'),
      required,
      modalTitle: this.$t('Create Whitelist'),
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'actions', label: 'Actions' },
      ],
      accountSearchParams: {
        name: null,
        field: null,
      },
      whiteListFormData: {
        accountName: '',
        accountNumber: '',
        id: '',
      },
      customerList: [],
      searchTableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'customerID', sortable: true, label: 'Customer ID' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
      ],
      filter: {
        accountNumber: '',
        bankCode: '',
      },
      whiteListData: [],
    }
  },
  computed: {
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedSearchTableHeaders() {
      return this.searchTableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedSearchFieldOptions() {
      return searchFieldOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    ...mapGetters({
      bankListOptions: 'eChannels/allBankOptions',
    }),
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchWhiteList()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchWhiteList()
    },
  },
  mounted() {
    this.fetchWhiteList()
  },
  methods: {
    ...mapActions({
      getWhiteList: 'fraudmonitor/getWhiteListAction',
      createWhiteList: 'fraudmonitor/createWhiteListAction',
      removeWhiteListItem: 'fraudmonitor/removeWhiteListItemAction',
      getByAccountNumber: 'account/getByAccountNumber',
      searchCustomerAccount: 'account/searchCustomerAccountAction',
    }),
    handleCustomerSearch() {
      try {
        this.$refs.whiteListRules.validate().then(async success => {
          if (success) {
            this.customerType = 'credit'
            this.showSpinner = true
            const searchParams = Object.values(this.accountSearchParams)
            const [searchValue, searchKey] = searchParams

            const searchData = {
              search: { [searchKey]: searchValue },
              page: this.currentPage,
              limit: this.perPage,
              permission: fraudMonitorPermissions.VIEW_WHITELIST,
            }

            const response = await this.searchCustomerAccount(searchData)
            const { status, data } = response
            if (status === 200) {
              this.customerList = data.data
              this.$root.$emit('bv::show::modal', 'search-modal')
            }
            this.showSpinner = false
          }
        })
        this.showSpinner = false
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.showSpinner = false
      }
    },
    onSelectCustomer(items) {
      this.hideModal('search-modal')
      const [customer] = items
      this.whiteListFormData = {
        ...this.whiteListFormData,
        accountName: customer.accountName,
        accountNumber: customer.accountNumber,
      }
    },
    async onRemoveItem(items) {
      const { id } = items
      const permission = fraudMonitorPermissions.VIEW_WHITELIST
      const deleteWhiteListItem = async () => {
        try {
          this.showSpinner = true
          const response = await this.removeWhiteListItem({ id, permission })
          const { status } = response
          if (status === 200) {
            this.fetchWhiteList()
            this.alertSuccess({ text: this.$t('Your request to remove from Whitelist was successful') })
            this.showSpinner = false
          }
        } catch (error) {
          this.handleError(error)
        } finally {
          this.showSpinner = false
        }
      }
      this.confirm(deleteWhiteListItem, { title: '', text: this.$t('Are you sure you want to Remove from Whitelist?') })
    },
    async fetchWhiteList() {
      try {
        this.isBusy = true
        this.hideModal('modal-filter')
        const response = await this.getWhiteList({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: fraudMonitorPermissions.VIEW_WHITELIST,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.whiteListData = data.data
          this.totalCount = data.recordCount
        } else {
          this.whiteListData = []
          this.totalCount = 0
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async whiteListAction() {
      try {
        this.isLoading = true
        this.showSpinner = true
        const formData = {
          ...this.whiteListFormData,
        }
        const response = await this.createWhiteList({ formData, permission: fraudMonitorPermissions.ADD_WHITELIST })
        const { status } = await response
        if (status === 200) {
          this.fetchWhiteList()
          this.hideModal('modal-lg')
          this.alertSuccess({
            text: this.$t('Your request to Add Whitelist was successful'),
          })
          this.showSpinner = false
        }
      } catch (error) {
        this.hideModal('modal-lg')
        const reasonText = this.$t('Your request to Add Whitelist failed. Reason:')
        error.response.data.errors.forEach(responseError => {
          this.alertError({ text: `${reasonText} ${responseError.errorMessage}` })
        })
      } finally {
        this.hideModal('modal-lg')
        this.showSpinner = false
      }
    },
    async handleSubmit(bvModalEvt) {
      try {
        bvModalEvt.preventDefault()
        this.$refs.whiteListRules.validate().then(async success => {
          if (success) {
            this.confirm(this.whiteListAction, {
              text: this.$t('Are you sure you want to Add to Whitelist?'),
            })
          }
        })
      } catch (error) {
        this.hideModal('modal-lg')
        this.alertError({ text: error })
      } finally {
        this.hideModal('modal-lg')
      }
    },
    resetModal() {
      this.whiteListFormData = { ...null }
      this.accountSearchParams = { ...null }
      this.isEditable = false
      this.isLoading = false
      this.isReadonly = false
      this.modalTitle = this.$t('Create Whitelist')
      this.buttonLabel = this.$t('Save')
    },
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchWhiteList()
    },
  },
}
</script>
